"use client";

import { useEffect, useRef, useState } from "react";
import { toast } from "@/components/toast";
import { loadPlacesLibrary } from "@/lib/google-maps";

export type AddressSelection = {
  formattedAddress: string;
  streetAddress: string;
  suburb: string;
  state: string;
  postcode: string;
  countryCode: string;
  coordinates: { lat: number; lng: number };
};

export type AddressSuggestion = {
  placeId: string;
  text: string;
  prediction: google.maps.places.PlacePrediction;
};

const DEBOUNCE_MS = 300;

type AddressComponent = {
  longText?: string | null;
  shortText?: string | null;
  types: string[];
};

const componentOf = (components: AddressComponent[], type: string, form: "long" | "short" = "long"): string => {
  const match = components.find((component) => component.types.includes(type));
  const value = form === "short" ? match?.shortText : match?.longText;
  return value ?? "";
};

const toStreetAddress = (components: AddressComponent[], formattedAddress: string): string => {
  const streetNumber = componentOf(components, "street_number");
  const route = componentOf(components, "route");
  const subpremise = componentOf(components, "subpremise");
  const street = [streetNumber, route].filter(Boolean).join(" ");

  if (!street) return formattedAddress.split(",")[0] ?? formattedAddress;

  return subpremise ? `${subpremise}/${street}` : street;
};

export const useAddressAutocomplete = () => {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const requestIdRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const runSearch = async (query: string) => {
    const requestId = ++requestIdRef.current;

    try {
      const { AutocompleteSuggestion, AutocompleteSessionToken } = await loadPlacesLibrary();
      sessionTokenRef.current ??= new AutocompleteSessionToken();

      const { suggestions: results } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: query,
        sessionToken: sessionTokenRef.current,
        includedRegionCodes: ["au"],
      });

      if (requestId !== requestIdRef.current) return;

      setSuggestions(
        results
          .filter((result) => result.placePrediction !== null)
          .map((result) => ({
            placeId: result.placePrediction!.placeId,
            text: result.placePrediction!.text.text,
            prediction: result.placePrediction!,
          })),
      );
    } catch {
      if (requestId !== requestIdRef.current) return;
      setSuggestions([]);
      toast.error("We couldn't search for that address. Please try again.");
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  };

  const search = (query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim()) {
      requestIdRef.current++;
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    debounceRef.current = setTimeout(() => void runSearch(query), DEBOUNCE_MS);
  };

  const clear = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    requestIdRef.current++;
    setSuggestions([]);
    setIsLoading(false);
  };

  const select = async (suggestion: AddressSuggestion): Promise<AddressSelection | null> => {
    try {
      const { place } = await suggestion.prediction.toPlace().fetchFields({
        fields: ["location", "formattedAddress", "addressComponents"],
      });

      sessionTokenRef.current = null;

      if (!place.location) {
        toast.error("That address doesn't have a precise location. Please choose another.");
        return null;
      }

      const components = (place.addressComponents ?? []) as AddressComponent[];
      const formattedAddress = place.formattedAddress ?? suggestion.text;

      return {
        formattedAddress,
        streetAddress: toStreetAddress(components, formattedAddress),
        suburb: componentOf(components, "locality") || componentOf(components, "sublocality"),
        state: componentOf(components, "administrative_area_level_1", "short"),
        postcode: componentOf(components, "postal_code"),
        countryCode: componentOf(components, "country", "short") || "AU",
        coordinates: { lat: place.location.lat(), lng: place.location.lng() },
      };
    } catch {
      toast.error("We couldn't fetch that address's details. Please try again.");
      return null;
    }
  };

  return { suggestions, isLoading, search, select, clear };
};
